import React, { useEffect } from 'react'
import PropTypes from 'prop-types'
import * as yup from 'yup'

import urlPathSetter from '@utils/urlPathSetter'
import getURLParams from '@utils/getURLParams'
import { useFormContext } from '@context/FormContext'

const serviceCards = [
  {
    value: 'hea',
    title: <>No-Cost Mass Save<sup style={{ fontSize: 10 }}>®</sup> Home Energy Assessment</>,
    description: 'A no-cost, whole-home audit to identify energy-savings opportunities, improve comfort, and qualify for rebates. 100% funded by the sponsors of Mass Save.',
    badge: 'No cost · No obligation',
    badgeStyle: { background: '#f0f8e0', color: '#5a7a1e' },
    iconBg: '#f0f8e0',
    icon: (
      <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
        <path d="M15 4L5 12v13a1 1 0 001 1h6v-6h6v6h6a1 1 0 001-1V12L15 4z" fill="#99c543" opacity="0.9" />
        <circle cx="20" cy="11" r="4" fill="#4675ad" opacity="0.7" />
        <path d="M19 11l1 1 2-2" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    ),
  },
  {
    value: 'hvac',
    title: 'HVAC Replacement Consultation',
    description: 'Get free, expert guidance on upgrading your heating and cooling system, plus available incentives and financing options.',
    badge: 'Free consultation',
    badgeStyle: { background: '#eef4fb', color: '#355a87' },
    iconBg: '#eef4fb',
    icon: (
      <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
        <rect x="5" y="8" width="20" height="14" rx="3" fill="#4675ad" opacity="0.8" />
        <rect x="8" y="11" width="14" height="8" rx="1.5" fill="white" opacity="0.9" />
        <circle cx="15" cy="15" r="2.5" fill="#4675ad" />
        <path d="M15 4v3M15 23v3M4 15H7M23 15h3" stroke="#4675ad" strokeWidth="1.5" strokeLinecap="round" />
      </svg>
    ),
  },
  {
    value: 'hvac-service',
    title: 'HVAC Repairs & Maintenance',
    description: 'Fix, tune-up, or maintain your existing heating and cooling equipment. Licensed technicians, fast scheduling.',
    badge: 'Repair · Tune-up · Maintenance',
    badgeStyle: { background: '#fef3ed', color: '#b04a1c' },
    iconBg: '#fef3ed',
    icon: (
      <svg width="30" height="30" viewBox="0 0 30 30" fill="none">
        <path d="M20 7l-3 3-7 7-2 5 5-2 7-7 3-3-3-3z" fill="#f2733b" opacity="0.85" />
        <path d="M17 10l3 3" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
        <circle cx="8" cy="22" r="2" fill="#f2733b" opacity="0.6" />
      </svg>
    ),
  },
]

const ChoosePath = ({ currentStepIndex, setValidationSchema }) => {
  const { values, setFieldValue } = useFormContext()
  urlPathSetter()
  const paramsObj = getURLParams()

  // Filter out HVAC options for HVAC-campaign UTMs
  const isHvacCampaign =
    paramsObj.utm_campaign && paramsObj.utm_campaign.includes('HVAC')

  const visibleCards = isHvacCampaign
    ? serviceCards.filter((c) => c.value === 'hea')
    : serviceCards

  const validValues = visibleCards.map((c) => c.value)

  useEffect(() => {
    setValidationSchema(
      yup.object().shape({
        path: yup
          .string()
          .oneOf(validValues)
          .required('Please select a service'),
      })
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepIndex, setValidationSchema])

  const handleSelect = (value) => {
    setFieldValue('path', value)
  }

  const selectedPath = values?.path

  return (
    <div className="max-w-2xl mx-auto px-4">
      {/* Header */}
      <div
        className="text-center mb-8"
        style={{ animation: 'fadeUp 0.5s cubic-bezier(0.22,1,0.36,1) both' }}
      >
        <h1
          className="text-3xl md:text-4xl text-stone-900 leading-tight mb-3"
          style={{ fontFamily: "'DM Serif Display', Georgia, serif", fontWeight: 400 }}
        >
          What brings you here today?
        </h1>
        <p className="text-stone-500 text-sm max-w-md mx-auto">
          Choose the service that fits your home&apos;s needs. All services are available to Massachusetts homeowners.
        </p>
      </div>

      {/* Service cards */}
      <div className="flex flex-col gap-4">
        {visibleCards.map((card) => {
          const isSelected = selectedPath === card.value
          return (
            <button
              key={card.value}
              type="button"
              onClick={() => handleSelect(card.value)}
              className={`service-card bg-white rounded-2xl p-5 border-2 flex items-start gap-4 text-left w-full${isSelected ? ' selected' : ''}`}
              style={{
                borderColor: isSelected ? '#99c543' : '#e7e5e4',
                boxShadow: isSelected
                  ? '0 0 0 2px #99c543, 0 4px 16px rgba(153,197,67,0.18)'
                  : '0 1px 4px rgba(0,0,0,0.06),0 4px 16px rgba(0,0,0,0.06)',
              }}
            >
              {/* Icon */}
              <div
                className="card-icon-wrap w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ background: isSelected ? '#f0f8e0' : card.iconBg }}
              >
                {card.icon}
              </div>

              {/* Content */}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-semibold text-stone-900 text-base leading-snug">
                      {card.title}
                    </div>
                    <div className="text-stone-500 text-sm mt-1 leading-relaxed">
                      {card.description}
                    </div>
                  </div>

                  {/* Check badge */}
                  <span
                    className="check-badge w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                    style={{ background: '#99c543' }}
                  >
                    <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                      <path d="M2 6l3 3 5-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                  </span>
                </div>

                <span
                  className="inline-flex items-center gap-1 mt-2 text-xs font-semibold px-2 py-0.5 rounded-full"
                  style={card.badgeStyle}
                >
                  {card.badge}
                </span>
              </div>
            </button>
          )
        })}
      </div>
    </div>
  )
}

ChoosePath.info = {
  title: 'Choose Service',
  key: 'choosePath',
}

ChoosePath.propTypes = {
  currentStepIndex: PropTypes.number.isRequired,
  setFieldValue: PropTypes.func,
  setValidationSchema: PropTypes.func.isRequired,
  values: PropTypes.object,
}

export default ChoosePath
